import toast from "react-hot-toast";
import { useDevAgentStore, type FeedItem } from "@/stores/devAgentStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";

const MIN_TOKENS_TO_COMPRESS = 30_000;

/** Same windowed estimate as AgentFeed — chars/3.5 after last context-compression marker. */
export function computeApproxContextTokens(items: FeedItem[]): number {
  let startIdx = 0;
  for (let i = items.length - 1; i >= 0; i--) {
    const it = items[i];
    if (
      it.type === "chat_agent" &&
      typeof it.data?.content === "string" &&
      it.data.content.startsWith("🗜️ **Context compressed**")
    ) {
      startIdx = i;
      break;
    }
  }
  let chars = 0;
  for (let i = startIdx; i < items.length; i++) {
    const f = items[i];
    if (f.type === "chat_user" || f.type === "chat_agent") {
      chars += (f.data.content || "").length;
    }
  }
  return Math.round(chars / 3.5);
}

/**
 * Client-side context compression: trim old feed rows, add a placeholder, then
 * POST the system compress prompt. Shared by the composer token control and
 * ChatHeaderBar "Compress".
 */
export async function runDevAgentContextCompress(): Promise<void> {
  const store = useDevAgentStore.getState();
  const items = store.feedItems;
  const totalTokens = computeApproxContextTokens(items);

  if (totalTokens < MIN_TOKENS_TO_COMPRESS) {
    toast("Context is small — no compression needed");
    return;
  }
  toast("Compressing context...");
  try {
    const uuid = store.sessionUuid;
    if (!uuid) {
      toast.error("No active session");
      return;
    }
    const toRemove = items.slice(0, -4);
    for (const item of toRemove) store.removeFeedItem(item.id);
    store.addFeedItem({
      type: "chat_agent",
      data: {
        content: "*(Earlier conversation compressed)*",
        readOnly: true,
      },
    });
    const { sendDevAgentMessage } = await import("../devAgentService");
    await sendDevAgentMessage(uuid, {
      user_query:
        "[SYSTEM:COMPRESS] Summarize the conversation so far and free up context.",
      org_id: useAuthStoreV1.getState().selectedOrg?.id,
    });
  } catch {
    toast.error("Compression failed");
  }
}
